import {
  ClauseMatchListRequestSchema,
  ENDPOINTS,
  type ClauseMatchListRequest,
  type ClauseMatchListResponse,
} from '@clm/shared-types';

import { apiGet, type Query } from './client';

/**
 * `clause-matches` — the FR-28 per-paragraph review queues, wrapped for the
 * browser (CLM-60; the API is CLM-34's).
 *
 * Same recipe as `clause-candidates.ts`: the path comes from the registry, the
 * request is `.parse`d before it leaves the browser (the request schema is
 * `.strict()`, so an invented or scope-shaped key is a ZodError here rather
 * than a parameter on the wire), and the response is **parsed** rather than
 * asserted so an off-contract answer is a defect to report, not data to show.
 *
 * The queue is scoped server-side on `root_contract_key` (NFR-06, rules 9–10);
 * the browser sends only the contract's own parameters — `queue`, an optional
 * `contract_key`, `page.*` — never a scope-shaped key.
 *
 * There is no write wrapper here on purpose: `resolve_clause_match` is one
 * member of the `manage-clause` union and `manageClause` (`clauses.ts`) already
 * posts the whole union.
 *
 * Encoding for the GET (`apps/api/src/http/decode.ts`): dotted keys for the
 * nested page object; `undefined` is omitted, never sent as `""`.
 */

const LIST_ENDPOINT = ENDPOINTS['clause-matches'];

/** Flatten a validated list request into the query the decoder expands. */
export function clauseMatchListQuery(request: ClauseMatchListRequest): Query {
  const query: Record<string, string | number> = { queue: request.queue };
  if (request.contract_key !== undefined) query['contract_key'] = request.contract_key;
  if (request.page?.limit !== undefined) query['page.limit'] = request.page.limit;
  if (request.page?.cursor !== undefined) query['page.cursor'] = request.page.cursor;
  return query;
}

export async function fetchClauseMatches(
  request: ClauseMatchListRequest,
  signal?: AbortSignal,
): Promise<ClauseMatchListResponse> {
  const validated = ClauseMatchListRequestSchema.parse(request);
  const raw = await apiGet<unknown>(LIST_ENDPOINT.path, {
    query: clauseMatchListQuery(validated),
    ...(signal === undefined ? {} : { signal }),
  });
  return LIST_ENDPOINT.response.parse(raw);
}
